import { useEffect } from 'react';
import { Navigate, useLocation, useParams, type Location } from 'react-router-dom';
import { useI18n } from '../i18n';
import { setActiveSlot } from '../lib/activeSlot';
import { useHealth } from '../lib/useHealth';
import { useAuth } from './AuthProvider';
import {
  decideGateway,
  gatewayTarget,
  hasGatewayAttempt,
  markGatewayAttempt,
} from './access-gateway';
import { useCanWrite } from './useCanWrite';

/**
 * Gardes de routes (multi-login T7c).
 *
 * `PrivateRoute` — 3 états, dans l'ordre : (1) chargement → spinner ; (2) panne
 * réseau → écran « réessayer » (JAMAIS de redirection vers /login sur un 5xx) ;
 * (3) non authentifié → passerelle (ADR-0008) si configurée, sinon /login (en
 * mémorisant la page visée).
 *
 * `WriteRoute` — défense en profondeur pour les PAGES d'outils d'écriture : un
 * non-writer (lecteur, rôle `user` sans bibliothèque) qui atteint l'URL directement
 * est REDIRIGÉ vers l'accueil (les affordances sont déjà masquées via `useCanWrite`,
 * et le serveur renvoie 403 ; ceci ferme aussi l'accès par URL). Suppose l'auth déjà
 * garantie en amont (`PrivateRoute` parent).
 *
 * `LegacySlotRedirect` — rétro-compat des anciens liens `/u/:slot/*` : pose le
 * slot puis redirige vers l'URL propre équivalente (le slot est local au navigateur,
 * hors de l'URL → liens partageables).
 */

function CenteredStatus({
  role,
  children,
}: {
  role: 'status' | 'alert';
  children: React.ReactNode;
}) {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-surface px-4 text-center text-muted">
      <div role={role}>{children}</div>
    </div>
  );
}

export function PrivateRoute({ children }: { children: React.ReactNode }): React.JSX.Element {
  const { t } = useI18n();
  const location = useLocation();
  const {
    user,
    loading,
    accountsLoading,
    accountsError,
    networkError,
    refreshAccounts,
    refreshMe,
  } = useAuth();

  if (accountsLoading || loading) {
    return <CenteredStatus role="status">{t('auth.checkingSession')}</CenteredStatus>;
  }

  if (networkError || accountsError) {
    return (
      <CenteredStatus role="alert">
        <p className="mb-4 text-fg">{t('auth.networkErrorTitle')}</p>
        <button
          type="button"
          onClick={() => {
            void refreshAccounts();
            void refreshMe();
          }}
          className="rounded-md bg-brand px-3 py-2 text-sm font-medium text-brand-fg transition-colors hover:bg-brand/90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand motion-reduce:transition-none"
        >
          {t('auth.retry')}
        </button>
      </CenteredStatus>
    );
  }

  if (!user) {
    return <UnauthenticatedRoute location={location} />;
  }

  return <>{children}</>;
}

/**
 * Visiteur SANS session : passerelle (ADR-0008) ou écran de connexion.
 *
 * Monté UNIQUEMENT quand il n'y a pas de session — le `useHealth()` qu'il déclenche
 * ne coûte donc rien au chemin nominal. La décision est prise par `decideGateway`
 * (pure) ; ce composant n'en est que la coquille.
 */
function UnauthenticatedRoute({ location }: { location: Location }): React.JSX.Element {
  const { t } = useI18n();
  const { health, error } = useHealth();
  const decision = decideGateway({
    health,
    healthError: error,
    target: gatewayTarget(location.pathname, location.search),
    attempted: hasGatewayAttempt(),
  });

  // Le marqueur anti-boucle est posé AVANT de quitter la page (au retour de pnyx, un
  // échange raté doit trouver la trace de la tentative). L'effet ne tourne que sur
  // 'redirect' ; `assign` (et non `replace`) laisse le « précédent » revenir en arrière.
  useEffect(() => {
    if (decision.kind !== 'redirect') return;
    markGatewayAttempt();
    window.location.assign(decision.url);
  }, [decision.kind, decision.kind === 'redirect' ? decision.url : null]);

  if (decision.kind === 'login') {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  // 'wait' (health en vol) et 'redirect' (navigation en cours) : même attente muette.
  return <CenteredStatus role="status">{t('auth.checkingSession')}</CenteredStatus>;
}

export function WriteRoute({ children }: { children: React.ReactNode }): React.JSX.Element {
  const canWrite = useCanWrite();
  if (!canWrite) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

export function LegacySlotRedirect(): React.JSX.Element {
  const { slot } = useParams();
  const location = useLocation();
  // Idempotent : pose le slot demandé avant de rediriger vers l'URL propre.
  if (slot !== undefined && /^\d+$/.test(slot)) {
    setActiveSlot(Number.parseInt(slot, 10));
  }
  const rest = location.pathname.replace(/^\/u\/\d+/, '') || '/';
  return <Navigate replace to={`${rest}${location.search}`} />;
}
